import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useMailboxes } from '../../../music24/outreach/hooks/useMailboxes';
import { ProviderMark } from '../../../music24/outreach/mailboxGuide/ProviderMark';
import type { MailboxRow } from '../../../music24/outreach/outreachTypes';
import { MailboxNotice } from './MailboxNotice';
import { MailboxRowMenu, type MailboxRowActions } from './MailboxRowMenu';
import { MailboxStatusPill } from './MailboxStatusPill';
import { TodayMeter } from './TodayMeter';
import { WarmupChart } from './WarmupChart';
import { addedByLine } from './mailboxLines';
import { mailboxMessages as messages } from './mailboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Mailboxes card; each part uses its own slice of it */
import styles from './Mailboxes.module.scss';

interface MailboxRowViewProps {
  mailbox: MailboxRow;
  isOwner: boolean;
  /** Opens the connect guide with this address ("Change the password", "Type a new password"). */
  onChangePassword: (mailbox: MailboxRow) => void;
}

/**
 * One mailbox (design outreach-v2 q1, q3) on one line: the provider mark, the address with
 * "<provider> · added by <who> on <date>", the warm-up chart (or today's meter after the warm-up),
 * the status pill and the more button. A paused or a sign-in failed row shows its alert under it.
 */
const MailboxRowViewComponent: React.FC<MailboxRowViewProps> = ({ mailbox, isOwner, onChangePassword }) => {
  const intl = useIntl();
  const { pauseMailbox, resumeMailbox, removeMailbox, sendTest, busyUuid } = useMailboxes();
  const { showError, showSuccess } = useShowToast();
  const uuid = mailbox.mailboxUuid;
  const isMine = mailbox.createdBy?.isYou === true;
  // Only Music24 support ends its own pause (owner decision 2026-10-12): no menu for that row.
  const isSupportPause = mailbox.health === 'paused' && mailbox.pauseCause === 'support';
  /** A refused change shows the sentence of the API (a support pause, a plan limit). */
  const fail = useCallback(
    (error: unknown) => {
      console.error(error);
      const text = error instanceof Error && error.message.trim() !== '' ? error.message : null;
      showError(text ?? intl.formatMessage(messages.actionFailed));
    },
    [intl, showError]
  );
  const actions = useMemo<MailboxRowActions>(
    () => ({
      test: () => {
        sendTest({ mailboxUuid: uuid })
          .then((result) =>
            result.ok
              ? showSuccess(intl.formatMessage(messages.testSent, { to: result.to }))
              : showError(intl.formatMessage(messages.testFailed))
          )
          .catch(fail);
      },
      pause: () => {
        pauseMailbox(uuid).catch(fail);
      },
      resume: () => {
        resumeMailbox(uuid).catch(fail);
      },
      changePassword: () => onChangePassword(mailbox),
      remove: () => {
        // eslint-disable-next-line no-alert -- a short confirm before a remove
        if (window.confirm(intl.formatMessage(messages.removeConfirm, { address: mailbox.address }))) {
          removeMailbox(uuid).catch(fail);
        }
      },
    }),
    [
      fail,
      intl,
      mailbox,
      onChangePassword,
      pauseMailbox,
      removeMailbox,
      resumeMailbox,
      sendTest,
      showError,
      showSuccess,
      uuid,
    ]
  );
  const showMenu = !isSupportPause && (isOwner || isMine);

  return (
    <div className={styles.row} data-testid="mailbox-row">
      <ProviderMark providerId={mailbox.providerId} large />
      <div className={styles.rowId}>
        <b>{mailbox.address}</b>
        <small data-testid="mailbox-added-by">{addedByLine(intl, mailbox)}</small>
      </div>
      <div className={styles.rowMeter}>
        {mailbox.warmup.warming && (mailbox.health === 'warming' || mailbox.health === 'ok') ? (
          <WarmupChart mailbox={mailbox} />
        ) : (
          <TodayMeter mailbox={mailbox} />
        )}
      </div>
      <div className={styles.rowSide}>
        <MailboxStatusPill health={mailbox.health} />
        {showMenu ? (
          <MailboxRowMenu mailbox={mailbox} isOwner={isOwner} disabled={busyUuid === uuid} actions={actions} />
        ) : (
          <span className={styles.menuSpace} aria-hidden="true" />
        )}
      </div>
      <MailboxNotice
        mailbox={mailbox}
        canResume={isOwner}
        canReconnect={isOwner || isMine}
        onResume={actions.resume}
        onReconnect={actions.changePassword}
      />
    </div>
  );
};

export const MailboxRowView = TypedMemo(MailboxRowViewComponent);
